import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { Button, Input, Table, message, Popover, Tabs, Tooltip } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import {
  CopyOutlined,
  PlusOutlined,
  DeleteOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import Dropdown from '../../../common/Dropdown';
import Icons from '../../Icons';
import Xmark from '../../../../../../assets/customSVG/Xmark';
import { useParams } from 'react-router-dom';
import { AttributeList } from '../../../../api/sequence';
import {
  useAspDispatch,
  useAspSelector,
} from '../../../../../../test/jest-redux-hooks';
import { cn } from 'helper/cn';
import { ICONS } from '../../../../../../assets/icons';
import { whatsappBusinessNumbers } from '../../../../../../api/Api';
import { copyText } from 'helper/text';
import Loader from '../../../../../../components/commonComponents/Loader/Index';
import { DAILY_MESSAGING_LIMIT } from '../../../../../inbox/constants/CampaignConstants';
import Duplicate from '../../../../../../assets/customSVG/Duplicate';

import { updateInitialTrigger } from '../../../../api/sequence';

// notification
import NotificationContext from '../../../../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../../../../components/commonComponents/gxAntdToast/toastUtils';
import { baseURL } from '../../../../../../api/ApiConfig';

const ApiWebhookConfig = ({
  node,
  onChange,
  toggleNodeEditMode,
  allowToEditFlow,
  sequenceDetails,
}) => {
  const { campaign_id, sequence_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isPayloadCopied, setIsPayloadCopied] = useState(false);
  const [phoneList, setPhoneList] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const { currentBrand } = useAspSelector((state) => state.app);
  // Get available attributes for dropdown (filter out already selected ones and mobile_number)
  const getAvailableAttributes = (currentMapping) => {
    return attributes.filter((attr) => {
      // Always exclude mobile_number field
      if (attr.name === 'mobile_number' || attr.name === 'country_code' || attr.name === 'age' || attr.name === 'whatsapp_opt_out' ) {
        return false;
      }
      // If this is the current mapping's attribute, include it
      if (
        currentMapping.crmField === attr.label ||
        currentMapping.crmFieldName === attr.name
      ) {
        return true;
      }
      // Otherwise, only include if it's not used in any other mapping
      return !mappings.some(
        (m) =>
          (m.crmField === attr.label || m.crmFieldName === attr.name) &&
          m.id !== currentMapping.id
      );
    });
  };

  const [mappings, setMappings] = useState(() => {
    const defaultMappings = [
      {
        id: 'mapping-mobile',
        crmField: 'Mobile Number',
        crmFieldName: 'mobile_number',
        mappingField: 'mobile_number',
        type: 'string',
        required: true,
        readOnly: true,
      },
      {
        id: 'mapping-country-code',
        crmField: 'Country Code',
        crmFieldName: 'country_code',
        mappingField: 'country_code',
        type: 'string',
        required: true,
        readOnly: true,
      },
    ];

    if (node?.data?.attribute_mapping) {
      // Filter out mobile_number from the saved mappings since we handle it separately
      const customMappings = Object.entries(node.data.attribute_mapping)
        .filter(([_, crmFieldName]) => crmFieldName !== 'mobile_number' && crmFieldName !== 'country_code')
        .map(([apiParam, crmFieldName], index) => {
          // Try to find the attribute by name first
          let attribute = attributes.find((attr) => attr.name === crmFieldName);
          
          // If not found by name, try to find by matching the field name in the attributes
          if (!attribute && attributes.length > 0) {
            attribute = attributes.find((attr) => attr.name === crmFieldName);
          }
          
          return {
            id: `mapping-${index}`,
            crmField: attribute?.label || crmFieldName,
            crmFieldName: crmFieldName,
            mappingField: apiParam,
            type: attribute?.type || 'string',
            required: false,
            readOnly: false,
          };
        });

      return [...defaultMappings, ...customMappings];
    } else {
      // Set default mappings with mobile_number
      return [
        ...defaultMappings,
        {
          id: 'mapping-0',
          crmField: '',
          crmFieldName: '',
          mappingField: '',
          type: 'string',
          required: false,
          readOnly: false,
        },
      ];
    }
  });

  // Check if all mappings are valid (both fields are filled)
  const areMappingsValid = useMemo(() => {
    if (mappings.length === 0) return false;

    const mappingFields = new Set();

    for (const mapping of mappings) {
      // Check if both fields are filled
      if (!mapping.crmField || !mapping.mappingField) {
        return false;
      }

      // Check for duplicate mapping fields
      if (mappingFields.has(mapping.mappingField)) {
        return false;
      }
      mappingFields.add(mapping.mappingField);
    }

    return true;
  }, [mappings]);

  // Update mappings when attributes are loaded
  useEffect(() => {
    if (attributes.length > 0) {
      setMappings((prevMappings) => {
        // Preserve the mobile_number mapping if it exists, or add it if missing
        const mobileMapping = prevMappings.find(m => m.crmFieldName === 'mobile_number') || {
          id: 'mapping-mobile',
          crmField: 'Mobile Number',
          crmFieldName: 'mobile_number',
          mappingField: 'mobile_number',
          type: 'string',
          required: true,
          readOnly: true,
        };

        // Process other mappings
        const otherMappings = prevMappings
          .filter(m => m.crmFieldName !== 'mobile_number')
          .map((mapping) => {
            // If the mapping's CRM field is not found in attributes, mark it as read-only
            if (
              mapping.crmField &&
              !attributes.some((a) => a.label === mapping.crmField)
            ) {
              const attribute = attributes.find(
                (a) => a.name === mapping.crmFieldName
              );
              if (attribute) {
                return {
                  ...mapping,
                  crmField: attribute.label,
                  type: attribute.type,
                };
              }
            }
            return mapping;
          });

        return [mobileMapping, ...otherMappings];
      });
    }
  }, [attributes, node?.data?.attribute_mapping]);

  const { id, uniq_id, trigger_type, data, settings } = node;

  const notification = useContext(NotificationContext);

  const webhookUrl = `${baseURL}api/v1/svc/campaign_sequence/sequences/trigger_sequence/${sequenceDetails?.slug}`;

  // Fetch WhatsApp business numbers
  const fetchPhoneNumbers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await whatsappBusinessNumbers(currentBrand?.brand_id);
      setPhoneList(res?.data?.wapp_business_num || []);
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      showErrorToast(notification, 'Failed to load WhatsApp numbers');
    } finally {
      setIsLoading(false);
    }
  }, [currentBrand?.brand_id]);

  // Fetch CRM attributes
  const fetchAttributes = useCallback(async () => {
    try {
      const res = await AttributeList(currentBrand?.brand_id);
      const attributesData = res?.data?.scopes_and_attributes_list || [];
      const allAttributes = [];

      // Process the nested attributes structure
      attributesData.forEach((scope) => {
        if (scope?.profile_attributes?.length) {
          scope.profile_attributes.forEach((attr) => {
            allAttributes.push({
              id: attr.name, // e.g., 'first_name'
              name: attr.name, // e.g., 'first_name'
              label: attr.label, // e.g., 'First Name'
              type: attr.attribute_type, // e.g., 'string'
              required: attr.default || false,
              scope: scope.name, // e.g., 'profile_information'
            });
          });
        }
      });
      setAttributes(allAttributes);
    } catch (error) {
      console.error('Error fetching attributes:', error);
      showErrorToast(notification, 'Failed to load CRM attributes');
    }
  }, [campaign_id]);

  useEffect(() => {
    fetchPhoneNumbers();
    fetchAttributes();
  }, [fetchPhoneNumbers, fetchAttributes]);

  const isOptionSelected = (option) => {
    return node?.data?.phone_number?.id === option?.id;
  };

  const handleCopy = () => {
    copyText(webhookUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000); // Hide the message after 2 seconds
  };

  const handleSave = async () => {
    if (!allowToEditFlow) return;

    // Validate required fields
    const requiredFields = mappings.filter(
      (m) => m.required && !m.mappingField
    );

    if (requiredFields.length > 0) {
      message.error(
        `Please fill in all required fields: ${requiredFields
          .map((f) => f.crmField)
          .join(', ')}`
      );
      return;
    }

    setLoading(true);

    try {
      // Prepare attribute mappings
      const attributeMapping = {};
      const expectedAttributes = [];

      // Process mappings to create expected_attributes and attribute_mapping
      mappings.forEach((mapping) => {
        // Find the full attribute object to get the name (e.g., 'first_name')
        // First try to find by name (for existing mappings)
        let attribute = attributes.find(
          (attr) => attr.name === mapping.crmField
        );

        // If not found by name, try by label (for new mappings)
        if (!attribute) {
          attribute = attributes.find(
            (attr) => attr.label === mapping.crmField
          );
        }

        if (attribute) {
          // Use the attribute name (e.g., 'first_name')
          const fieldName = attribute.name;

          // Add to expected_attributes if not already present
          if (!expectedAttributes.includes(fieldName)) {
            expectedAttributes.push(fieldName);
          }

          // Add to attribute_mapping if mappingField exists
          if (mapping.mappingField) {
            // The key is the API parameter name, value is the CRM field name
            attributeMapping[mapping.mappingField] = fieldName;
          }
        }
      });

      // Prepare payload
      const payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id,
        trigger_type: 'API_TRIGGER',
        save: true,
        details: {
          phone_number: node?.data?.phone_number,
          webhook_url: webhookUrl,
          method: 'POST',
          expected_attributes: [...new Set(expectedAttributes)], // Remove duplicates
          attribute_mapping: attributeMapping,
        },
        remove_edges: false,
      };

      // Make API call
      const res = await updateInitialTrigger(sequence_id, node?.uniq_id, {
        ...payload,
        defaults: {
          ...node?.settings?.defaults,
          isEditMode: false,
        },
      });

      if (res?.status === 200) {
        // Create updated node with response data
        const responseData = res?.data?.data?.[0] || {};

        // const updatedNode = {
        //   ...node, // Preserve existing node data
        //   ...responseData, // Add/override with response data
        //   trigger_type: 'API_TRIGGER',
        //   label: 'API Trigger',
        //   data: {
        //     ...node?.data, // Preserve existing data
        //     ...responseData, // Add/override with response data
        //     name: 'API Trigger',
        //     webhook_url: webhookUrl,
        //     method: 'POST',
        //     attribute_mapping: attributeMapping,
        //     expected_attributes: expectedAttributes,
        //   },
        //   settings: {
        //     ...node?.settings,
        //     defaults: {
        //       ...node?.settings?.defaults,
        //       isEditMode: false,
        //     },
        //   },
        // };

        // Update node data in parent
        onChange({
          type: 'UPDATE_NODE_DATA',
          id: node?.uniq_id,
          data: responseData,
        });

        // Close the edit mode
        toggleNodeEditMode({
          id: node?.id,
          uniqId: node?.uniq_id,
          value: 'false',
          defaults: {
            ...node?.settings?.defaults,
            isEditMode: false,
          },
        });

        showSuccessToast(
          notification,
          'API Trigger configuration saved successfully'
        );
      } else {
        throw new Error(
          res?.response?.data?.error || 'Failed to save configuration'
        );
      }
    } catch (error) {
      console.error('Error saving API trigger configuration:', error);
      showErrorToast(
        notification,
        error.response?.data?.message ||
          error.message ||
          'Failed to save configuration'
      );
    } finally {
      setLoading(false);
    }
  };

  // Format and validate mapping field input
  const formatMappingField = (value) => {
    // Remove any non-alphanumeric characters except underscores
    let formatted = value.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Ensure it starts with a letter
    if (formatted.length > 0 && !/^[a-zA-Z]/.test(formatted)) {
      formatted = 'param_' + formatted;
    }
    
    // Limit to 25 characters
    return formatted.slice(0, 25);
  };

  const handleMappingChange = (id, field, value) => {
    setMappings((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          // When changing CRM field, also update the crmFieldName
          if (field === 'crmField') {
            const attribute = attributes.find((attr) => attr.label === value);
            return {
              ...item,
              [field]: value,
              crmFieldName: attribute ? attribute.name : value,
            };
          }
          // When changing mapping field, apply formatting and validation
          if (field === 'mappingField') {
            // Format the value according to API parameter naming conventions
            const cleanValue = formatMappingField(value);
            return { ...item, [field]: cleanValue };
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
  };

  // Check for duplicate mapping fields
  const hasDuplicateMappingFields = useMemo(() => {
    const mappingFields = new Set();
    for (const mapping of mappings) {
      if (!mapping.mappingField) continue;
      if (mappingFields.has(mapping.mappingField)) {
        return true;
      }
      mappingFields.add(mapping.mappingField);
    }
    return false;
  }, [mappings]);

  const hasIncompleteMappings = useMemo(() => {
    return mappings.some(({ crmField, mappingField }) =>
      (crmField && !mappingField) || (!crmField && mappingField)
    );
  }, [mappings]);  

  const isSaveDisabled = useMemo(() => {
    return (
      hasIncompleteMappings ||
      hasDuplicateMappingFields ||
      !node?.data?.phone_number ||
      !allowToEditFlow
    );
  }, [
    hasIncompleteMappings,
    hasDuplicateMappingFields,
    node?.data?.phone_number,
    allowToEditFlow,
  ]);

  const addNewMapping = () => {
    // Don't add a new mapping if the last one is empty
    if (mappings.length > 0 && !mappings[mappings.length - 1].crmField) {
      return;
    }
    
    setMappings([
      ...mappings,
      {
        id: `mapping-${Date.now()}`,
        crmField: '',
        crmFieldName: '',
        mappingField: '',
        type: 'string',
        required: false,
        readOnly: false,
      },
    ]);
  };

  const removeMapping = (id) => {
    const itemToRemove = mappings.find(m => m.id === id);
    if (itemToRemove?.required) return;
    setMappings((prev) => prev.filter((item) => item.id !== id));
  };

  const columns = [
    {
      title: 'CRM Field',
      dataIndex: 'crmField',
      key: 'crmField',
      width: '40%',
      render: (text, record) => {
        const availableAttributes = getAvailableAttributes(record);
        return (
          <div className="flex items-center gap-2">
            {record.required && <span className="text-red-500">*</span>}
            {record.readOnly ? (
              <span>{text}</span>
            ) : (
              <Dropdown
                value={record.crmField}
                onChange={(value) =>
                  handleMappingChange(record.id, 'crmField', value)
                }
                options={availableAttributes.map((attr) => ({
                  value: attr.label,
                  label: attr.label,
                  disabled: record.required || record.readOnly,
                }))}
                placeholder="Select Field"
                disabled={record.required || record.readOnly}
                className="w-full"
                dropdownClassName="min-w-[300px]"
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Mapping Field',
      dataIndex: 'mappingField',
      key: 'mappingField',
      width: '40%',
      render: (text, record) => (
        <div className="relative">
          <Input
            value={text}
            onChange={(e) =>
              handleMappingChange(record.id, 'mappingField', e.target.value)
            }
            placeholder="Enter API parameter name"
            disabled={!allowToEditFlow || record.readOnly}
            className="w-full pr-12"
            maxLength={25}
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
            {text?.length || 0}/25
          </div>
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: '10%',
      render: (_, record) =>
        !record.required && (
          <Button
            type="text"
            danger
            onClick={() => removeMapping(record.id)}
            disabled={!allowToEditFlow}
            icon={<span className="text-xs font-bold">✕</span>}
          />
        ),
    },
  ];

  const payloadPreview = useMemo(() => {
    return {
      ...mappings.reduce((acc, curr) => {
        if (curr.mappingField) {
          acc[curr.mappingField] = 'value';
        }
        return acc;
      }, {}),
    };
  }, [mappings]);

  const handleCancel = () => {
    toggleNodeEditMode({
      id: node?.id,
      uniqId: node?.uniq_id,
      value: 'false',
      defaults: { ...node?.settings?.defaults, isEditMode: false },
    });
  };

  // -------------------- RENDER ------------------------

  return (
    <div className="w-full flex-1 min-w-full max-w-[42rem] h-full rounded-2xl overflow-hidden select-none">
      {/* Header */}
      <div className="flex items-center justify-between gap-5 px-5 py-2.5 bg-[#04B8A2] text-white">
        <div className="flex items-center gap-5">
          <Icons keyProp="api_trigger" bgProp="transparent" />
          <p className="capitalize">API Trigger</p>
        </div>
        <div
          className="size-4 flex items-center justify-center cursor-pointer"
          onClick={handleCancel}
        >
          <Xmark color="white" width={12} height={12} />
        </div>
      </div>

      <div className="w-full pt-[12px] bg-white">
        <div className="w-full max-h-[500px] px-[12px] flex flex-col gap-2 overflow-y-auto listScroll">
          {/* WhatsApp Number */}
          <div className="p-6 border-b w-full">
            <h3 className="text-base font-[600] text-gray-800 mb-1">
              WhatsApp Number
            </h3>
            <Popover
              trigger="click"
              open={isOpen}
              onOpenChange={(open) => setIsOpen(open)}
              placement="bottomLeft"
              arrow={false}
              content={
                <div className="w-full bg-white rounded-2xl z-50">
                  {isLoading ? (
                    <div className="min-w-[48vh] h-16 flex flex-1 items-center justify-center rounded-2xl cursor-pointer">
                      <Loader Width={25} Height={25} />
                    </div>
                  ) : (
                    <ul
                      className={`w-full min-w-[48vh] m-0 list-none rounded-2xl flex-col gap-1.5 overflow-y-scroll listScroll bg-[var(--white)] z-50 p-2.5 ${
                        isOpen ? 'flex' : 'hidden'
                      }`}
                    >
                      {isLoading ? (
                        <div className="w-100 h-[7vh] flex-row align-center justify-center">
                          <Loader Width={25} Height={25} />
                        </div>
                      ) : (
                        <>
                          {phoneList?.map((option) => {
                            return (
                              <li
                                key={option?.id}
                                className="w-full h-[80px] flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border-100)] last:border-0 cursor-pointer"
                                onClick={() => {
                                  setIsOpen(false);
                                  onChange({
                                    type: 'UPDATE_NODE',
                                    id: node?.uniq_id,
                                    key: 'data',
                                    data: {
                                      ...node?.data,
                                      phone_number: {
                                        ...option,
                                        send_from_phone_name:
                                          option?.send_from_phone_name,
                                        send_from_phone_number:
                                          option?.send_from_phone_number,
                                        send_from_phone_number_id:
                                          option?.send_from_phone_number_id,
                                        country_code: option?.country_code,
                                      },
                                    },
                                  });
                                }}
                              >
                                <div className="flex flex-col gap-2.5">
                                  <div className="flex items-center justify-between gap-5">
                                    <p>{option?.send_from_phone_name}</p>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 text-sm weight-medium text-[#616874] pr-3.5">
                                      +{option?.country_code + ' ' + option?.send_from_phone_number}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)]  border-x border-[var(--border-100)] px-3.5">
                                      <p className="size-2.5 bg-[var(--primary)] rounded-sm"></p>
                                      <p>High</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs weight-semibold text-[var(--contentText)] px-3.5">
                                      {
                                        DAILY_MESSAGING_LIMIT[
                                          option?.daily_messaging_limit
                                        ]
                                      }
                                      /24hr
                                    </div>
                                  </div>
                                </div>
                                {isOptionSelected(option) && (
                                  <img
                                    src={ICONS?.Selected}
                                    alt="selected"
                                    className="size-5"
                                  />
                                )}
                              </li>
                            );
                          })}
                        </>
                      )}
                    </ul>
                  )}
                </div>
              }
            >
              <div
                className="w-full h-12 flex items-center justify-between border border-gray-200 px-4 py-2 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
              >
                {node?.data?.phone_number?.send_from_phone_name ? (
                  <div className="flex items-center gap-2">
                    <span className="font-[500]">
                      {node.data.phone_number.send_from_phone_name}
                    </span>
                    <span className="text-gray-500">
                      +{node.data.phone_number.country_code + ' ' +
                        node.data.phone_number.send_from_phone_number}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400">Select WhatsApp number</span>
                )}
                <img
                  src={ICONS?.modelDropdownIcon}
                  className={cn(
                    'transition-transform duration-200',
                    isOpen ? 'rotate-180' : 'rotate-0'
                  )}
                  alt="dropdown"
                />
              </div>
            </Popover>
            <p className="text-xs text-gray-500 mt-2">
              Select the WhatsApp number that will be used to send messages
            </p>
          </div>

          {/* Webhook URL */}
          <div className="p-6 border-b">
            <h3 className="text-base font-[600] text-gray-800 mb-3">
              Webhook URL
            </h3>
            <div className="relative max-w-3xl">
              <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-gray-300">
                <div className="px-3 py-2.5 bg-gray-50 border-r border-gray-200">
                  <LinkOutlined className="text-gray-500" />
                </div>
                <input
                  value={webhookUrl}
                  readOnly
                  className="flex-1 px-3 py-2.5 text-sm text-gray-700 bg-transparent border-0 w-full"
                  aria-label="Webhook URL"
                />
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200 rounded"
                  aria-label="Copy to clipboard"
                  type="button"
                >
                  <div className="relative w-5 h-5">
                    <Duplicate
                      width={18}
                      height={18}
                      className={`absolute inset-0 m-auto transition-all duration-200 ${
                        isCopied ? 'opacity-0 scale-90' : 'opacity-100'
                      }`}
                    />
                    <div
                      className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
                        isCopied
                          ? 'opacity-100 text-green-500'
                          : 'opacity-0 -translate-y-1'
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500 flex flex flex-col">
                {isCopied && (
                  <div className="ml-2 text-sm font-[500] text-green-600 flex items-center flex-start transition-all duration-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Copied to clipboard!
                  </div>
                )}
                <div>
                  Use this URL to trigger this sequence via a POST request
                </div>
              </p>
            </div>
          </div>

          {/* Mandatory Fields */}
          <div className="px-6 py-3 bg-gray-50 rounded-lg">
            <h4 className="font-[600] text-[15px] text-gray-700 mb-2">Mandatory Fields</h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start">
                <CheckCircleOutlined className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <code className="bg-gray-100 px-1 py-0.5 rounded">
                    mobile_number
                  </code>{' '}
                  - Recipient's phone number (e.g., "9876543210")
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircleOutlined className="text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <code className="bg-gray-100 px-1 py-0.5 rounded">
                    country_code
                  </code>{' '}
                  - Recipient's country code (e.g., "1" for US, "91" for India)
                </div>
              </div>
              {/* <p>
                •{' '}
                <code className="bg-gray-100 px-2 py-0.5 rounded">
                  sequence_id
                </code>{' '}
                - This sequence ID:{' '}
                <code className="bg-gray-100 px-2 py-0.5 rounded">
                  {sequence_id}
                </code>
              </p> */}
            </div>
          </div>

          {/* CRM Field Mapping */}
          <div className="p-6 border-b">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-[600] text-gray-800">
                  CRM Field Mapping
                </h3>
                <Tooltip 
                  title="Map your API request fields to CRM contact fields. This allows you to automatically save contact information from API requests to your CRM."
                  placement="right"
                >
                  <img
                    src={ICONS?.infoIconViolet || ICONS?.info}
                    alt="info"
                    className="w-4 h-4 cursor-help"
                  />
                </Tooltip>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={addNewMapping}
                    className="flex items-center gap-1 !bg-[#6564CA] hover:!bg-[#6564CA] active:!bg-[#6564CA] focus:!bg-[#6564CA] !border-[#6564CA] hover:!border-[#6564CA] !text-white hover:!text-white active:!text-white focus:!text-white"
                    icon={<PlusOutlined />}
                  >
                    Add New Field
                  </Button>
                </div>
                {/* {hasIncompleteMappings && mappings.length > 0 && (
                  <div className="text-red-500 text-sm">
                    Error: Please fill in all CRM fields and mapping fields.
                  </div>
                )} */}
              </div>
            </div>

            <div className="mt-4">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <Table
                  columns={columns}
                  dataSource={mappings}
                  size="small"
                  pagination={false}
                  rowKey="id"
                  className="rounded-lg"
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }
                />
              </div>
              {hasDuplicateMappingFields && (
                <div className="text-red-500 text-sm">
                  Error: Duplicate mapping fields found. Each mapping field must
                  be unique.
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Map your CRM fields to the API parameters. Fields marked with{' '}
                <span className="text-red-500">*</span> are required.
              </p>
            </div>
          </div>

          {/* Payload Preview */}
          <div className="px-6 py-3 border-b">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-base font-[600] text-gray-800">Request Payload</h3>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(payloadPreview, null, 2));
                  setIsPayloadCopied(true);
                  setTimeout(() => setIsPayloadCopied(false), 2000);
                }}
                className="p-1 -mt-1 -mr-1 text-gray-500 hover:text-gray-700 transition-colors duration-200 rounded"
                aria-label="Copy payload to clipboard"
                type="button"
              >
                <div className="relative w-5 h-5">
                  <svg
                    className={`absolute inset-0 m-auto transition-all duration-200 ${
                      isPayloadCopied ? 'opacity-0 scale-90' : 'opacity-100'
                    }`}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
                      isPayloadCopied
                        ? 'opacity-100 text-green-500'
                        : 'opacity-0 -translate-y-1'
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
            <div className="bg-white p-4 rounded border border-gray-200 relative">
              <pre className="text-xs text-gray-800 overflow-x-auto">
                {JSON.stringify(payloadPreview, null, 2)}
              </pre>
            </div>
            {/* <Tabs defaultActiveKey="payload"> */}
              {/* <Tabs.TabPane tab="Payload" key="payload"> */}
              {/* <Tabs.TabPane tab="cURL Example" key="curl">
                <div className="bg-gray-800 text-green-400 p-4 rounded-md overflow-x-auto text-xs">
                  <pre className="whitespace-pre-wrap">
                    {`curl -X POST \\
  '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '{
    ${mappings
      .filter((m) => m.mappingField)
      .map(
        (m, i, arr) =>
          `    "${m.mappingField}": "value"${i < arr.length - 1 ? ',' : ''}`
      )
      .join('\n')}
  }'`}
                  </pre>
                </div>
              </Tabs.TabPane> */}
            {/* </Tabs> */}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 px-6 py-5 bg-gray-50 border-t rounded-b-2xl">
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              loading={loading}
              onClick={handleSave}
              disabled={isSaveDisabled}
              className={`!bg-[#6564CA] hover:!bg-[#6564CA] active:!bg-[#6564CA] focus:!bg-[#6564CA] !border-[#6564CA] hover:!border-[#6564CA] !text-white hover:!text-white active:!text-white focus:!text-white px-8 ${isSaveDisabled ? 'opacity-50' : ''}`}
              title={
                isSaveDisabled
                  ? hasDuplicateMappingFields
                    ? 'Please fix duplicate mapping fields'
                    : 'Please fill in all required fields'
                  : ''
              }
            >
              Save Configuration
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiWebhookConfig;
